import { memo } from 'react';
import { ProgressBar as RACProgressBar, ProgressBarProps as RACProgressBarProps } from 'react-aria-components';

import { ComponentContextProps, merge, renderWithContext, useComponent } from '@/components';
import { DivProps } from '@/utils';

export type ProgressBarProps = RACProgressBarProps &
  Partial<ComponentContextProps> & {
    props?: {
      barOverlay?: DivProps;
      barBackground?: DivProps;
    };
    barStyle?: 'default' | 'striped' | 'striped-tight';
  };

const IProgressBar = ({ props, variant, barStyle = 'default', className, children, ...rest }: ProgressBarProps) => {
  const context = useComponent({ variant });

  /* prettier-ignore */
  const styles = [
    'flex',
    'flex-col',
    'gap-2',
  ];

  /* prettier-ignore */
  const barBackgroundStyles= [
    'h-2',
    barStyle === 'default' && 'rounded-full',
    'transform',
    'w-full',
  ];

  /* prettier-ignore */
  const stripedBackgroudStyles = [
    barStyle === 'striped' && 'bg-[repeating-linear-gradient(to_right,var(--color-gray-300)_0px,var(--color-gray-300)_3px,var(--color-white)_3px,var(--color-white)_6px)]',
    barStyle === 'striped-tight' && 'bg-[repeating-linear-gradient(to_right,var(--color-gray-300)_0px,var(--color-gray-300)_2px,var(--color-white)_2px,var(--color-white)_3px)]',
  ];

  /* prettier-ignore */
  const barBackgroundPlainStyles = [
    barStyle === 'default' && 'bg-gray-100',
    ['striped', 'striped-tight'].includes(barStyle) && stripedBackgroudStyles,
  ];

  /* prettier-ignore */
  const barBackgroundPrimaryStyles = [
    barStyle === 'default' && 'bg-primary-100',
    ['striped', 'striped-tight'].includes(barStyle) && stripedBackgroudStyles,
  ];

  /* prettier-ignore */
  const barBackgroundSecondaryStyles = [
    barStyle === 'default' && 'bg-gray-100',
    ['striped', 'striped-tight'].includes(barStyle) && stripedBackgroudStyles,
  ];

  /* prettier-ignore */
  const barBackgroundSuccessStyles = [
    barStyle === 'default' && 'bg-success-100',
    ['striped', 'striped-tight'].includes(barStyle) && stripedBackgroudStyles,
  ];

  /* prettier-ignore */
  const barBackgroundWarningStyles = [
    barStyle === 'default' && 'bg-warning-100',
    ['striped', 'striped-tight'].includes(barStyle) && stripedBackgroudStyles,
  ];

  /* prettier-ignore */
  const barBackgroundErrorStyles = [
    barStyle === 'default' && 'bg-error-100',
    ['striped', 'striped-tight'].includes(barStyle) && stripedBackgroudStyles,
  ];

  /* prettier-ignore */
  const barOverlayStyles= [
    'relative',
    'top-0',
    'h-2',
    'transform',
    barStyle === 'default' && 'rounded-full',
  ];

  /* prettier-ignore */
  const barOverlayPlainStyles = [
    barStyle === 'default' && 'bg-gray-500',
    barStyle === 'striped' && 'bg-[repeating-linear-gradient(to_right,var(--color-gray-500)_0px,var(--color-gray-500)_3px,var(--color-white)_3px,var(--color-white)_6px)]',
    barStyle === 'striped-tight' && 'bg-[repeating-linear-gradient(to_right,var(--color-gray-500)_0px,var(--color-gray-500)_2px,var(--color-white)_2px,var(--color-white)_3px)]',
  ];

  /* prettier-ignore */
  const barOverlayPrimaryStyles = [
    barStyle === 'default' && 'bg-primary-500',
    barStyle === 'striped' && 'bg-[repeating-linear-gradient(to_right,var(--color-primary-500)_0px,var(--color-primary-500)_3px,var(--color-white)_3px,var(--color-white)_6px)]',
    barStyle === 'striped-tight' && 'bg-[repeating-linear-gradient(to_right,var(--color-primary-500)_0px,var(--color-primary-500)_2px,var(--color-white)_2px,var(--color-white)_3px)]',
  ];

  /* prettier-ignore */
  const barOverlaySecondaryStyles = [
    barStyle === 'default' && 'bg-gray-500',
    barStyle === 'striped' && 'bg-[repeating-linear-gradient(to_right,var(--color-gray-500)_0px,var(--color-gray-500)_3px,var(--color-white)_3px,var(--color-white)_6px)]',
    barStyle === 'striped-tight' && 'bg-[repeating-linear-gradient(to_right,var(--color-gray-500)_0px,var(--color-gray-500)_2px,var(--color-white)_2px,var(--color-white)_3px)]',
  ];

  /* prettier-ignore */
  const barOverlaySuccessStyles = [
    barStyle === 'default' && 'bg-success-500',
    barStyle === 'striped' && 'bg-[repeating-linear-gradient(to_right,var(--color-success-500)_0px,var(--color-success-500)_3px,var(--color-white)_3px,var(--color-white)_6px)]',
    barStyle === 'striped-tight' && 'bg-[repeating-linear-gradient(to_right,var(--color-success-500)_0px,var(--color-success-500)_2px,var(--color-white)_2px,var(--color-white)_3px)]',
  ];

  /* prettier-ignore */
  const barOverlayWarningStyles = [
    barStyle === 'default' && 'bg-warning-500',
    barStyle === 'striped' && 'bg-[repeating-linear-gradient(to_right,var(--color-warning-500)_0px,var(--color-warning-500)_3px,var(--color-white)_3px,var(--color-white)_6px)]',
    barStyle === 'striped-tight' && 'bg-[repeating-linear-gradient(to_right,var(--color-warning-500)_0px,var(--color-warning-500)_2px,var(--color-white)_2px,var(--color-white)_3px)]',
  ];

  /* prettier-ignore */
  const barOverlayErrorStyles = [
    barStyle === 'default' && 'bg-error-500',
    barStyle === 'striped' && 'bg-[repeating-linear-gradient(to_right,var(--color-error-500)_0px,var(--color-error-500)_3px,var(--color-white)_3px,var(--color-white)_6px)]',
    barStyle === 'striped-tight' && 'bg-[repeating-linear-gradient(to_right,var(--color-error-500)_0px,var(--color-error-500)_2px,var(--color-white)_2px,var(--color-white)_3px)]',
  ];

  const { className: barOverlayClassName, ...barOverlayProps } = props?.barOverlay ?? {};
  const { className: barBackgroundClassName, ...barBackgroundProps } = props?.barBackground ?? {};

  return (
    <RACProgressBar aria-label="progressbar" className={merge(styles, className)} {...rest}>
      {({ percentage, valueText, isIndeterminate, ...rest }) => (
        <>
          {renderWithContext(children, context, { percentage, valueText, isIndeterminate, ...rest })}
          <div
            className={merge(
              barBackgroundStyles,
              context.variant === 'default' && barBackgroundPlainStyles,
              context.variant === 'plain' && barBackgroundPlainStyles,
              context.variant === 'primary' && barBackgroundPrimaryStyles,
              context.variant === 'secondary' && barBackgroundSecondaryStyles,
              context.variant === 'success' && barBackgroundSuccessStyles,
              context.variant === 'warning' && barBackgroundWarningStyles,
              context.variant === 'error' && barBackgroundErrorStyles,
              barBackgroundClassName
            )}
            {...barBackgroundProps}
          >
            <div
              className={merge(
                barOverlayStyles,
                context.variant === 'default' && barOverlayPlainStyles,
                context.variant === 'plain' && barOverlayPlainStyles,
                context.variant === 'primary' && barOverlayPrimaryStyles,
                context.variant === 'secondary' && barOverlaySecondaryStyles,
                context.variant === 'success' && barOverlaySuccessStyles,
                context.variant === 'warning' && barOverlayWarningStyles,
                context.variant === 'error' && barOverlayErrorStyles,
                barOverlayClassName
              )}
              style={{ width: percentage + '%' }}
              {...barOverlayProps}
            />
          </div>
        </>
      )}
    </RACProgressBar>
  );
};

export const ProgressBar = memo(IProgressBar) as typeof IProgressBar;
